import React, { useEffect, useState } from "react";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { Section, SectionLabel, SectionTitle } from "@/components/layout/Section";
import { Reveal } from "@/lib/motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CAREERS } from "@/constants/testIds";
import { listOpportunities, submitTalentNetwork } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { ArrowRight, Briefcase, UserPlus, Check, Loader2, ShieldCheck, Lock, BellRing } from "lucide-react";

const HERO_IMG = "/hero-careers.jpg";
const HERO_IMG_WEBP = "/hero-careers.webp";

export default function CareersPage() {
  const [opps, setOpps] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    listOpportunities().then((r) => setOpps(r.opportunities || [])).catch(() => setOpps([]));
  }, []);

  return (
    <SiteLayout>
      <Toaster theme="dark" position="top-center" richColors closeButton />

      {/* HERO */}
      <section className="relative min-h-[600px] sm:min-h-[680px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <picture>
            <source srcSet={HERO_IMG_WEBP} type="image/webp" />
            <img
              src={HERO_IMG}
              alt="Team in collaboration around a shared workspace"
              className="h-full w-full object-cover object-center"
              loading="eager"
              fetchpriority="high"
            />
          </picture>
          {/* Editorial left-to-right dark gradient so the copy breathes */}
          <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--background))] via-[hsl(var(--background))]/85 to-[hsl(var(--background))]/25" />
          <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--background))] via-transparent to-[hsl(var(--background))]/30" />
          <div aria-hidden className="absolute inset-0 brp-hero-glow" />
        </div>
        <div className="relative mx-auto w-full max-w-7xl px-5 sm:px-8 lg:px-10 py-20 sm:py-28">
          <Reveal><div className="text-[11px] tracking-[0.28em] uppercase text-[hsl(var(--brp-blue))] flex items-center gap-3"><span className="h-px w-8 bg-[hsl(var(--brp-blue))]" /> Careers</div></Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-6 font-display font-semibold tracking-[-0.03em] text-4xl sm:text-6xl lg:text-7xl leading-[1.02] max-w-3xl">
              Build What’s <span className="text-[hsl(var(--brp-blue))]">Next</span>.
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-xl text-[hsl(var(--brp-ink-dim))] leading-relaxed">
              Technology changes quickly. Great builders don’t. BRP Tech is growing a team of thoughtful problem solvers, creators, strategists, and innovators committed to building technology that creates meaningful impact.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8">
              <Button
                onClick={() => setOpen(true)}
                data-testid={CAREERS.openTalentNetwork}
                className="rounded-full bg-[hsl(var(--brp-blue))] hover:bg-[#2563EB] text-white px-7 h-12"
              >
                Join Our Talent Network <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* OPPORTUNITIES + STAY CONNECTED */}
      <Section tone="dark" py="lg" className="border-t border-border/70">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Explore Opportunities */}
          <Reveal className="lg:col-span-6">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg border border-border/70 bg-background/40 grid place-items-center text-[hsl(var(--brp-blue))]"><Briefcase className="h-4 w-4" /></div>
              <SectionTitle size="md">Explore Opportunities</SectionTitle>
            </div>
            <p className="mt-4 text-[hsl(var(--brp-ink-dim))] max-w-md">Current opportunities will appear here as BRP Tech continues to grow.</p>

            {opps.length === 0 ? (
              <div className="mt-8 rounded-2xl border border-dashed border-border/70 p-8 bg-[hsl(var(--brp-surface-2))] text-center">
                <div className="mx-auto h-12 w-12 rounded-full border border-border/70 grid place-items-center text-[hsl(var(--brp-blue))]">
                  <UserPlus className="h-5 w-5" />
                </div>
                <p className="mt-6 text-[hsl(var(--brp-ink))]">There are no opportunities available at this time.</p>
                <p className="mt-2 text-sm text-[hsl(var(--brp-ink-dim))] max-w-sm mx-auto">We’re growing intentionally and will publish new opportunities here as they become available.</p>
                <button
                  onClick={() => setOpen(true)}
                  className="mt-6 inline-flex items-center gap-2 text-[13px] uppercase tracking-wide text-[hsl(var(--brp-blue))] hover:text-[hsl(var(--brp-blue-2))]"
                >
                  Join Our Talent Network <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="mt-6 space-y-3">
                {opps.map((o) => (
                  <div key={o.id || o.title} className="rounded-xl border border-border/70 bg-[hsl(var(--brp-surface-2))] p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-[11px] tracking-widest uppercase text-[hsl(var(--brp-blue))]">{o.department}</div>
                        <h3 className="mt-1 font-display font-semibold">{o.title}</h3>
                        <p className="mt-1 text-sm text-[hsl(var(--brp-ink-dim))]">{o.location} — {o.employment_type}</p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-[hsl(var(--brp-blue))]" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Reveal>

          {/* Stay Connected */}
          <Reveal className="lg:col-span-6" delay={0.05}>
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg border border-border/70 bg-background/40 grid place-items-center text-[hsl(var(--brp-blue))]"><UserPlus className="h-4 w-4" /></div>
              <SectionTitle size="md">Stay Connected.</SectionTitle>
            </div>
            <p className="mt-4 text-[hsl(var(--brp-ink-dim))] leading-relaxed max-w-md">
              Exceptional teams are built over time. If you’re interested in future opportunities with BRP Tech, we’d love to learn more about you.
            </p>
            <div className="mt-6">
              <Button onClick={() => setOpen(true)} className="rounded-full bg-[hsl(var(--brp-blue))] hover:bg-[#2563EB] text-white px-6 h-11">
                Join Our Talent Network <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: ShieldCheck, label: "Your information is secure." },
                { icon: Lock, label: "We respect your privacy." },
                { icon: BellRing, label: "We’ll reach out when opportunities align." },
              ].map((r) => (
                <div key={r.label} className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-full border border-border/70 grid place-items-center text-[hsl(var(--brp-blue))] shrink-0"><r.icon className="h-3.5 w-3.5" /></div>
                  <span className="text-[13px] text-[hsl(var(--brp-ink-dim))] leading-snug">{r.label}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </Section>

      {/* Growing with Intention */}
      <Section tone="paper" py="lg">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <Reveal className="lg:col-span-7">
            <SectionLabel className="!text-[hsl(var(--brp-orange))]">Growing with Intention</SectionLabel>
            <SectionTitle className="mt-4" size="xl">Building a company worth joining.</SectionTitle>
            <p className="mt-6 text-[hsl(var(--brp-ink-muted-on-paper))] leading-relaxed">
              We’re building BRP Tech thoughtfully and intentionally. As our company evolves, we’ll continue looking for people who care deeply about solving meaningful problems, embracing curiosity, and creating technology that leaves a lasting impact.
            </p>
            <p className="mt-4 text-[hsl(var(--brp-ink-muted-on-paper))] leading-relaxed">
              Whether you build software, design experiences, solve operational challenges, develop products, or think strategically about what’s possible, we’d love to stay connected as new opportunities emerge.
            </p>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.08}>
            <ul className="space-y-4">
              {["Meaningful Work", "Continuous Growth", "Thoughtful Impact", "Diverse Perspectives", "A Culture of Builders"].map((s) => (
                <li key={s} className="flex items-center gap-3 text-[hsl(var(--brp-ink-on-paper))]">
                  <span className="h-6 w-6 rounded-full bg-[hsl(var(--brp-blue))]/15 text-[hsl(var(--brp-blue))] grid place-items-center"><Check className="h-3.5 w-3.5" /></span>
                  <span className="font-medium">{s}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </Section>

      {/* Closing */}
      <Section tone="dark" py="md">
        <div className="rounded-2xl border border-border/70 bg-[hsl(var(--brp-surface-2))] p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <SectionLabel tone="blue"><span className="inline-flex items-center gap-3"><span className="h-px w-8 bg-[hsl(var(--brp-blue))]" /> Build with purpose.</span></SectionLabel>
            <p className="mt-4 font-display text-2xl sm:text-3xl tracking-tight">Technology changes.</p>
            <p className="font-display text-2xl sm:text-3xl tracking-tight text-[hsl(var(--brp-blue))]">Meaningful work doesn’t.</p>
          </div>
          <Button onClick={() => setOpen(true)} className="rounded-full bg-[hsl(var(--brp-blue))] hover:bg-[#2563EB] text-white px-6 h-11">Join Our Talent Network</Button>
        </div>
      </Section>

      <TalentNetworkDialog open={open} onOpenChange={setOpen} />
    </SiteLayout>
  );
}

function TalentNetworkDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({ full_name: "", email: "", role: "", linkedin: "", resume_url: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});

  function reset() {
    setForm({ full_name: "", email: "", role: "", linkedin: "", resume_url: "", message: "" });
    setErrors({});
    setDone(false);
  }

  function validate() {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Please enter your full name.";
    if (!form.email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = "Please enter a valid email.";
    if (!form.role.trim()) e.role = "Please share your current role or area of expertise.";
    if (!form.message.trim()) e.message = "Please tell us a little about yourself.";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit() {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await submitTalentNetwork({
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        role: form.role.trim(),
        linkedin: form.linkedin.trim() || null,
        resume_url: form.resume_url.trim() || null,
        message: form.message.trim(),
      });
      setDone(true);
      toast.success("Thanks for connecting with BRP Tech.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setTimeout(reset, 200); }}>
      <DialogContent className="bg-[hsl(var(--brp-surface-2))] border-border/70 max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Join Our Talent Network</DialogTitle>
          <DialogDescription className="text-[hsl(var(--brp-ink-dim))]">
            Exceptional teams are built over time. Share a little about yourself and we’ll be in touch as opportunities align.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-8 text-center">
            <div className="mx-auto h-12 w-12 rounded-full bg-emerald-500/15 text-emerald-400 grid place-items-center">
              <Check className="h-6 w-6" />
            </div>
            <p className="mt-4 font-display text-xl">You’re on our list.</p>
            <p className="mt-2 text-sm text-[hsl(var(--brp-ink-dim))] max-w-md mx-auto">We’ll reach out when opportunities that match your background become available.</p>
            <Button className="mt-6 rounded-full" variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
            <div>
              <Label className="text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--brp-ink-dim))]">Full Name</Label>
              <Input className="mt-2 bg-[hsl(var(--brp-surface-3))] border-border/70" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Enter your full name" />
              {errors.full_name && <p className="mt-2 text-xs text-red-400">{errors.full_name}</p>}
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--brp-ink-dim))]">Email Address</Label>
              <Input type="email" className="mt-2 bg-[hsl(var(--brp-surface-3))] border-border/70" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" />
              {errors.email && <p className="mt-2 text-xs text-red-400">{errors.email}</p>}
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--brp-ink-dim))]">Current Role or Area of Expertise</Label>
              <Input className="mt-2 bg-[hsl(var(--brp-surface-3))] border-border/70" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="e.g. Senior Product Designer, Full-stack Engineer, Strategy" />
              {errors.role && <p className="mt-2 text-xs text-red-400">{errors.role}</p>}
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--brp-ink-dim))]">LinkedIn or Portfolio (Optional)</Label>
              <Input className="mt-2 bg-[hsl(var(--brp-surface-3))] border-border/70" value={form.linkedin} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} placeholder="https://linkedin.com/in/…" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--brp-ink-dim))]">Resume Link (Optional)</Label>
              <Input className="mt-2 bg-[hsl(var(--brp-surface-3))] border-border/70" value={form.resume_url} onChange={(e) => setForm({ ...form, resume_url: e.target.value })} placeholder="Public link to your resume/CV" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--brp-ink-dim))]">Tell Us About Yourself</Label>
              <Textarea rows={4} className="mt-2 bg-[hsl(var(--brp-surface-3))] border-border/70" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Share what you’re passionate about, the kinds of problems you love solving, and what you’re looking for next." />
              {errors.message && <p className="mt-2 text-xs text-red-400">{errors.message}</p>}
            </div>
            <div className="sm:col-span-2 flex items-center justify-end gap-3 mt-2">
              <Button variant="outline" className="rounded-full border-border/70" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={submit} disabled={submitting} data-testid={CAREERS.talentSubmit} className="rounded-full bg-[hsl(var(--brp-blue))] hover:bg-[#2563EB] text-white px-6">
                {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…</>) : "Submit"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
